import { useQuery } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import api from '@/features/api/axios';
import { logger } from '@/features/help/utils/logger';

interface SalesforceRecord {
  id: string;
  name: string;
  email: string;
  [key: string]: any;
}

interface UseRecordsSearchProps {
  mappingId: string | null;
  enabled?: boolean;
}

export const useRecordsSearch = ({ mappingId, enabled = true }: UseRecordsSearchProps) => {
  const [searchQuery, setSearchQuery] = useState('');

  const {
    data: allRecords = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['records', mappingId],
    queryFn: async () => {
      if (!mappingId) return [];
      
      logger.debug('useRecordsSearch', 'Fetching records for mapping', { mappingId });
      
      const response = await api.get(`/api/crm/records/${mappingId}`, {
        params: {
          page: 1,
          pageSize: 100, // Get more records for better search experience
        }
      });
      
      return response.data.records || [];
    },
    enabled: enabled && !!mappingId,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // Client-side filtering for better UX
  const filteredRecords = useMemo(() => {
    if (!searchQuery.trim()) return allRecords;
    
    const query = searchQuery.toLowerCase();
    return allRecords.filter((record: SalesforceRecord) => 
      record.name?.toLowerCase().includes(query) ||
      record.email?.toLowerCase().includes(query)
    );
  }, [allRecords, searchQuery]);

  return {
    records: filteredRecords,
    searchQuery,
    setSearchQuery,
    isLoading,
    error,
  };
};
